import {addDevice, deleteDevice, getAllDevicesFromCustomer, getDevices, updateDevice} from "../api/devices";
import {InPaceProductFormValues} from "../components/forms/productForms/InPaceProductForm";
import dayjs from "dayjs";
import CustomerService from "./CustomerService";
import {formatDate} from "../utils/helperFunctions";
import {ClientDevice, DbCustomer} from "sharedlibs";
import {InPaceTableValues} from "../models/InPaceTableValues";

export default class InPaceService {
  static async getInPaceDevices(query?: any): Promise<ClientDevice[]> {
    const devices = await getAllDevicesFromCustomer(query)

    if (!Array.isArray(devices)) {
      throw new Error("Fetched inPaceDevices are not valid")
    }

    return devices
  }

  static async addDevice(customer: DbCustomer, formObject: InPaceProductFormValues) {
    const device = this.createNewDevice(customer, formObject)

    if (!(await this.checkUniqueProperties(device))) {
      alert("Some values which must be unique are not unique! Device will not be added")
      throw new Error("inPace values are not unique!")
    }

    console.log('Validation passed. Adding device')
    await addDevice(device)
  }

  static async updateDevice(oldDevice: ClientDevice, formObject: InPaceProductFormValues) {
    const updatedDevice = this.updateClientDeviceValues(oldDevice, formObject)

    if (!(await this.checkUniqueProperties(updatedDevice))) {
      alert("Some values which must be unique are not unique! Device will not be updated")
      throw new Error("inPace values are not unique!")
    }

    console.log('Validation passed. Updating device')
    await updateDevice(updatedDevice)
  }

  static async deleteDevice(inPaceDeviceId: string) {
    await deleteDevice(inPaceDeviceId)
  }

  static isLicenceValid(startDate: string, endDate: string) {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    return today >= start && today <= end;
  }

  static async getTableDeviceData(): Promise<InPaceTableValues[]> {
    const devices = await getDevices()
    const customers = await CustomerService.getAllCustomers()
    return devices.map(elem => {
      if (!elem.id) {
        throw new Error(`InPace device: ${String(elem)} does not have property .id\n` +
          "This is necessary to update or delete an inPace device.")
      }

      const regardingCustomer = customers.find((customer) => customer.id === elem.customerId)

      if (regardingCustomer === undefined) {
        throw new Error(`Customer object of inPace device: ${JSON.stringify(elem)} could not be found!`)
      }

      return {
        id: elem.id,
        customerId: elem.customerId,
        customerName: regardingCustomer.name,
        customerNumber: regardingCustomer.customerNumber,
        location: regardingCustomer.address.city,
        serialNr: elem.serialNr,
        softwareVn: elem.softwareVn,
        manufacturer: elem.manufacturer,
        deviceName: elem.deviceName,
        licenceStart: formatDate(elem.licenceStart),
        licenceEnd: formatDate(elem.licenceEnd),
        dateOfActivation: formatDate(elem.dateOfActivation),
        licenceValid: InPaceService.isLicenceValid(elem.licenceStart, elem.licenceEnd),
        additionalNotes: elem.additionalNotes,
        soldToBiotronik: elem.soldToBiotronik,
        deviceType: elem.deviceType,

        numberOfSubdevices: elem.subDevice.length
      }
    })
  }

  private static createNewDevice(customer: DbCustomer, formObject: InPaceProductFormValues): ClientDevice {
    if (customer.id === undefined) {
      throw new Error("Cannot add an inPace device if customerId is not provided.")
    }

    return {
      additionalNotes: formObject.additionalNotes,
      customerId: customer.id,
      customerName: customer.name,
      customerNumber: customer.customerNumber,
      dateOfActivation: formObject.dateOfActivation ? formObject.dateOfActivation.format('YYYY-MM-DD') : "",
      deviceName: formObject.deviceName,
      deviceType: formObject.deviceType,
      licenceEnd: formObject.licenceEnd ? formObject.licenceEnd.format('YYYY-MM-DD') : "",
      licenceStart: formObject.licenceStart ? formObject.licenceStart.format('YYYY-MM-DD') : "",
      location: customer.address.city,
      manufacturer: formObject.manufacturer,
      serialNr: formObject.serialNr,
      softwareVn: formObject.softwareVn,
      soldToBiotronik: formObject.soldToBiotronik,
      subDevice: formObject.subDevice,
    }
  }

  private static updateClientDeviceValues(device: ClientDevice, formObject: InPaceProductFormValues): ClientDevice{
    if (device.id === undefined) {
      throw new Error("Cannot update a device which has no id.")
    }

    return {
      id: device.id,
      additionalNotes: formObject.additionalNotes,
      customerId: device.customerId,
      customerName: device.customerName,
      customerNumber: device.customerNumber,
      dateOfActivation: formObject.dateOfActivation ? formObject.dateOfActivation.format('YYYY-MM-DD') : "",
      deviceName: formObject.deviceName,
      deviceType: formObject.deviceType,
      licenceEnd: formObject.licenceEnd ? formObject.licenceEnd.format('YYYY-MM-DD') : "",
      licenceStart: formObject.licenceStart ? formObject.licenceStart.format('YYYY-MM-DD') : "",
      location: device.location,
      manufacturer: formObject.manufacturer,
      serialNr: formObject.serialNr,
      softwareVn: formObject.softwareVn,
      soldToBiotronik: formObject.soldToBiotronik,
      subDevice: formObject.subDevice
    }
  }

  static mapDeviceObjectToFormObject(data: ClientDevice): InPaceProductFormValues {
    return {
      additionalNotes: data.additionalNotes,
      dateOfActivation: dayjs(data.dateOfActivation),
      deviceName: data.deviceName,
      deviceType: data.deviceType,
      licenceEnd: dayjs(data.licenceEnd),
      licenceStart: dayjs(data.licenceStart),
      manufacturer: data.manufacturer,
      serialNr: data.serialNr,
      softwareVn: data.softwareVn,
      soldToBiotronik: data.soldToBiotronik,
      subDevice: data.subDevice
    }
  }

  /**
   * This function checks properties which must be unique in the inPace object.
   * At the moment, this is only the "serialNr" property.
   * Returns true if values are unique, false otherwise.
   * @param device
   */
  static async checkUniqueProperties(device: ClientDevice): Promise<boolean> {
    const result = await getDevices()
    const notUnique = result.find((elem: any) => {
      return elem.serialNr === device.serialNr && elem.id !== device.id
    })
    return !notUnique
  }
}
